import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Web3 } from 'web3';

import { useWatchAssets } from '@pkg/hooks/nft/useWatchAssets';
import { useAppDispatch } from '@pkg/hooks/redux';

import { type ArtifactSignatureParams, ArtifactType, Events } from '@app/client';
import { callGenerateSignature } from '@app/client/resources/nft/artifacts';
import { callSetName } from '@app/client/resources/nft/nft';
import config from '@app/config';
import { getOpenSeaMintedYatUrl, prefetch2FA, yatAbi } from '@app/helpers';
import { registerEvent } from '@app/store/actions/events';
import { setLootBoxTokenIdMapping } from '@app/store/actions/minting';
import { setOpenSeaUrl } from '@app/store/actions/nft';
import { type LootBoxId } from '@app/store/types/artifacts';
import { useMintingOverlayStore } from '@app/store/zustand';

const { tokenContractAddress } = config;
interface GenerateSignatureArgs {
    value: ArtifactSignatureParams;
    type: ArtifactType;
    id: string;
}
interface MintTokenArgs {
    account: string;
    token: string;
    type?: ArtifactType;
    yatName?: string;
}
export function useGenerateSignature() {
    const dispatch = useAppDispatch();
    const { setMintingInitiated } = useMintingOverlayStore();

    return useMutation({
        mutationFn: (args: GenerateSignatureArgs) =>
            callGenerateSignature({ ...args, prefetch: prefetch2FA(dispatch, false, () => setMintingInitiated(true)) }),
        onError: () => false,
    });
}

export function useSetYatName() {
    return useMutation({
        mutationFn: ({ name, eid }: { name: string; eid: string }) => callSetName({ value: { data: { name } }, eid }),
    });
}

export function useMint(artifactTokenContractAddress = tokenContractAddress) {
    const dispatch = useAppDispatch();
    const { setMintingFailed, setMintingInitiated, setMintingSuccessOptimistic } = useMintingOverlayStore();
    const { mutateAsync: generateSignature } = useGenerateSignature();

    const watchAssets = useWatchAssets();
    const web3 = new Web3({
        provider: window.ethereum,
        config: {
            handleRevert: true,
        },
    });
    const YatContract = new web3.eth.Contract(yatAbi, artifactTokenContractAddress);

    const handleMint = useCallback(
        async (type: ArtifactType, from: string, token: string) => {
            try {
                const signatureRes = await generateSignature({ value: { account: from }, type, id: token });
                if (signatureRes?.ok) {
                    const { expiry, signature } = signatureRes.value;
                    try {
                        const mintRes = await YatContract.methods.mint(token, from, expiry, signature).send({
                            from,
                        });

                        // mintRes.status : BigInt(0) | BigInt(1)
                        const statusOk = Boolean(Number(mintRes.status));
                        if (statusOk) {
                            const userBalanceRes = await YatContract.methods.balanceOf(from).call({ from });
                            const userBalance = Number(userBalanceRes);
                            if (type === ArtifactType.Yat) {
                                registerEvent(Events.yat_minted, { yat_unicode: token });
                            }
                            if (userBalance > 0) {
                                const lastTokenId = await YatContract.methods
                                    .tokenOfOwnerByIndex(from, userBalance - 1)
                                    .call();

                                if (lastTokenId) {
                                    setMintingSuccessOptimistic(true);
                                    if (type !== ArtifactType.Yat) {
                                        registerEvent(Events.artifact_minted);
                                        dispatch(setLootBoxTokenIdMapping({ [token as LootBoxId]: lastTokenId }));
                                    }
                                    const url = getOpenSeaMintedYatUrl(lastTokenId.toString());
                                    dispatch(setOpenSeaUrl(url));
                                    const added = await watchAssets({
                                        tokenId: lastTokenId.toString(),
                                        address: tokenContractAddress,
                                    });
                                    if (added) {
                                        return true;
                                    }
                                }
                            }
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(`Mint error: ${JSON.stringify(e)}`);
                        return false;
                    }
                } else {
                    return false;
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Signature error: ${e}`);
                return false;
            }
        },
        [YatContract.methods, dispatch, generateSignature, setMintingSuccessOptimistic, watchAssets],
    );
    const mintYat = useCallback(
        async ({ account, token }: MintTokenArgs) => {
            setMintingInitiated(true);
            const mintYatSuccess = await handleMint(ArtifactType.Yat, account, token);
            if (mintYatSuccess === undefined) {
                setMintingInitiated(true);
            } else {
                if (mintYatSuccess) {
                    setMintingInitiated(false);
                } else if (!mintYatSuccess) {
                    setMintingFailed(true);
                }
            }
        },
        [handleMint, setMintingFailed, setMintingInitiated],
    );
    const mintArtifact = useCallback(
        async ({ type = ArtifactType.PrismCase, account, token }: MintTokenArgs) => {
            const mintArtifactSuccess = await handleMint(type, account, token);
            if (mintArtifactSuccess) {
                registerEvent(Events.artifact_minted);
            }
        },
        [handleMint],
    );
    return { mintYat, mintArtifact };
}
